import {EventEmitter, inject, Injectable, PLATFORM_ID, signal, WritableSignal} from '@angular/core';
import {UserModel} from '../../models/user.model';
import {Share} from '@capacitor/share';
import {LoadingController, ModalController, Platform, ToastController} from '@ionic/angular';
import {monthsNumbers} from '../utils';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Clipboard} from '@capacitor/clipboard';
import {Camera, CameraResultType} from '@capacitor/camera';
import {fileUploadSize} from 'src/app/services/utils';
import {LoginModalComponent} from "../../modal/login-modal/login-modal.component";
import {isPlatformBrowser} from "@angular/common";
import {Capacitor} from "@capacitor/core";

@Injectable({
  providedIn: 'root'
})
export class GlobalService {

  loading: HTMLIonLoadingElement;
  selectedLang: string;
  private loginModal: any;
  public sidePanelOpen: WritableSignal<boolean> = signal(true);

  public breakPointShowComponents: number = 1140;
  public breakPointMobile: number = 460;
  public breakPointBigScreen: number = 1920;
  public isLoadingWeb = signal(true);
  public homeContentEvent = new BehaviorSubject<any>(0);
  public backButtonEvent = new BehaviorSubject<any>(0);
  public languageEvent = new BehaviorSubject<any>('');
  public teamAPEvent = new BehaviorSubject<any>(0);
  public companyAPEvent = new BehaviorSubject<any>(0);

  public teamMyAPEvent = new BehaviorSubject<any>(0);
  public companyMyAPEvent = new BehaviorSubject<any>(0);

  public sdgsEvent = new BehaviorSubject<any>(0);
  public postactionsurveyEvent = new BehaviorSubject<any>(null);
  public postactionsurveySubject: Observable<any> = this.postactionsurveyEvent.asObservable();

  public homeScrollTop = new BehaviorSubject(null);

  public teamJoinedEvent = new BehaviorSubject<any>(null);

  public updateUser: EventEmitter<any> = new EventEmitter<any>();
  public isBrowser: boolean = true;
  public lastRoute: string = '';

  private platform: Platform = inject(Platform)
  private platformId: Object = inject(PLATFORM_ID);
  private loadingController: LoadingController = inject(LoadingController);
  private translate: TranslateService = inject(TranslateService);
  private toastController: ToastController = inject(ToastController);
  private modalController: ModalController = inject(ModalController);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      this.selectedLang = localStorage.getItem('selectLanguage') || 'de';
    } else {
      this.selectedLang = 'en';
    }
  }

  public get user(): UserModel {
    if (this.isBrowser) {
      const storageItem = localStorage.getItem('user');
      if (storageItem) {
        return JSON.parse(storageItem);
      }
    }
    return null;

  }

  public get isWeb(): boolean {
    return !Capacitor.isNativePlatform();
  }

  public async presentToast(msg, time?) {
    const toast = await this.toastController.create({
      message: msg,
      duration: time ? time : 2000
    });
    await toast.present();
  }

  async writeToClipboard(text) {
    await Clipboard.write({
      // eslint-disable-next-line id-blacklist
      string: text
    });
    await this.presentToast('Text copy in clipboard');
  };

  public async inviteFriends(data) {
    try {
      if (Capacitor.isNativePlatform()) {
        await Share.share({
          title: data.inviteTitle,
          text: data.inviteText,
          url: data.inviteUrl,
          dialogTitle: this.translate.instant('lbl_share_with_buddies'),
        });
      } else {
        await navigator.clipboard.writeText(data.inviteTitle + '\n' + data.inviteText + '\n' + data.inviteUrl);
        await this.presentToast(this.translate.instant('lbl_copied_to_clipboard'), 2000)
      }
    } catch (error) {
      console.error('Error', error);
    }
  }

  public timeSince(dateInput: string | number | Date): string {
    const lang: string = this.isBrowser
      ? (localStorage.getItem('selectLanguage') || 'de')
      : 'en';

    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "always" });

    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.abs((date.getTime() - now.getTime()) / 1000);

    const intervals: { unit: Intl.RelativeTimeFormatUnit, secs: number }[] = [
      { unit: 'year', secs: 31536000 },
      { unit: 'month', secs: 2592000 },
      { unit: 'week', secs: 604800 },
      { unit: 'day', secs: 86400 },
      { unit: 'hour', secs: 3600 },
      { unit: 'minute', secs: 60 },
      { unit: 'second', secs: 1 },
    ];

    for (const i of intervals) {
      const value: number = Math.floor(seconds / i.secs);
      if (value >= 1) {
        return rtf.format(-value, i.unit);
      }
    }
  }

  async presentLoading() {
    this.loading = await this.loadingController.create({
      cssClass: 'my-custom-class',
      message: 'Please wait...',
      /*duration: 1000*/
    });
    await this.loading.present();
  }

  async dismissLoading() {
    await this.loading.dismiss();
  }

  dateFormatted(dateFromIonDatetime) {
    const date = new Date(dateFromIonDatetime);
    return date.getDate() + '.' + monthsNumbers[date.getMonth()] + '.' + date.getFullYear();
  }

  dateTimeFormatted(dateFromIonDatetime) {
    const date = new Date(dateFromIonDatetime);
    const hours = date.getHours();

    return date.getDate() + '.' + monthsNumbers[date.getMonth()] + '.' +
      date.getFullYear() + ' ' + hours + ':' + date.getMinutes();
  }

  notificationTime(date) {
    const date2 = new Date();
    const diff = date2.getTime() - (new Date(date).getTime());
    const seconds = Math.floor((diff) / 1000);
    let interval: number = seconds / 86400;
    if (interval > 1) {
      if (this.selectedLang === 'de') {
        return this.translate.instant('lbl_ago') + ' ' + Math.floor(interval) + ' ' + this.translate.instant('lbl_days');
      } else {
        return Math.floor(interval) + ' ' + this.translate.instant('lbl_days') + ' ' + this.translate.instant('lbl_ago');
      }
    }

    interval = seconds / 3600;
    if (interval > 1) {
      if (this.selectedLang === 'de') {
        return this.translate.instant('lbl_ago') + ' ' + Math.floor(interval) + ' ' + this.translate.instant('lbl_hours');
      } else {
        return Math.floor(interval) + ' ' + this.translate.instant('lbl_hours') + ' ' + this.translate.instant('lbl_ago');
      }
    }
    interval = seconds / 60;
    if (interval > 1) {
      if (this.selectedLang === 'de') {
        return this.translate.instant('lbl_ago') + ' ' + Math.floor(interval) + ' ' + this.translate.instant('lbl_minutes');
      } else {
        return Math.floor(interval) + ' ' + this.translate.instant('lbl_minutes') + ' ' + this.translate.instant('lbl_ago');
      }
    }

    if (this.selectedLang === 'de') {
      return this.translate.instant('lbl_ago') + ' ' + Math.floor(interval) + ' ' + this.translate.instant('lbl_seconds');
    } else {
      return Math.floor(interval) + ' ' + this.translate.instant('lbl_seconds') + ' ' + this.translate.instant('lbl_ago');
    }
  }

  calculateImageSize(base64String) {
    let padding;
    if (base64String.endsWith('==')) {
      padding = 2;
    } else if (base64String.endsWith('=')) {
      padding = 1;
    } else {
      padding = 0;
    }

    const base64StringLength = base64String.length;
    return (base64StringLength / 4) * 3 - padding;
  }

  async takePhoto() {
    if (Capacitor.isNativePlatform()) {
      let imageImpactScoreSource: string;
      const errorMessage = [];
      const permission = await Camera.checkPermissions();

      if (permission.photos !== 'granted') {
        console.log('Permission', permission);
      }

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        width: 1920,
        height: 1080,
        resultType: CameraResultType.DataUrl
      });

      imageImpactScoreSource = image.dataUrl;
      const selectedFile = await fetch(imageImpactScoreSource).then(r => r.blob());
      if (this.calculateImageSize(imageImpactScoreSource) > fileUploadSize) {
        errorMessage.push(this.translate.instant('err_file_size_limit'));
        imageImpactScoreSource = '';
      }

      return {selectedFile, imageImpactScoreSource, errorMessage};
    } else {
      const errorMessage = [];
      let imageImpactScoreSource = '';
      const fileInput = document.getElementById('globalUploadImage');

      const handleFileChange = (e) => {
        return new Promise((resolve, reject) => {
          const file = e.target.files[0];
          const reader = new FileReader();
          reader.onload = (theFileData) => {
            if (typeof theFileData.target.result === "string") {
              imageImpactScoreSource = theFileData.target.result;
              resolve(imageImpactScoreSource);
            } else {
              reject(new Error('File data is not a string'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      const waitForFileSelection = () => {
        return new Promise((resolve) => {
          fileInput.onchange = (e) => {
            handleFileChange(e)
              .then(resolve)
              .catch((error) => {
                errorMessage.push(error.message);
                console.error(error);
                resolve(null);
              });
          };
          fileInput.click();
        });
      };

      await waitForFileSelection();

      let selectedFile;
      if (imageImpactScoreSource) {
        selectedFile = await fetch(imageImpactScoreSource).then(r => r.blob());
        if (this.calculateImageSize(imageImpactScoreSource) > fileUploadSize) {
          errorMessage.push(this.translate.instant('err_file_size_limit'));
          imageImpactScoreSource = '';
        }
      }

      return {selectedFile, imageImpactScoreSource, errorMessage};
    }
  }

  async openLoginModal() {
    if (this.loginModal) {
    }

    this.loginModal = await this.modalController.create({
      component: LoginModalComponent,
      backdropDismiss: true,
      mode: 'ios',
      id: 'loginToViewModal',
    });

    await this.loginModal.present();
    await this.loginModal.onDidDismiss();
  }

  public isMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;

    return this.platform.is('mobile')
      || this.platform.is('hybrid')
      || this.platform.is('ios')
      || this.platform.is('android')
      || this.isSmallScreen();
  }

  private isSmallScreen(): boolean {
    return window.innerWidth < 768;
  }

}
